
import { defineComponent } from "vue";
import LoadingScreen from "@/components/LoadingScreen.vue";
import "@/assets/scss/patient/records.scss";
import axios from "axios";
import { Carousel, Slide, Pagination, Navigation } from "vue3-carousel";
import "vue3-carousel/dist/carousel.css";

type Summary = Record<string, string | number>;

type TestResult = {
  id: number | string;
  label?: string;
  description?: string;
  image_path: string;
};

type TestRecord = {
  id: number;
  image_path: string;
  type: string;
  result_summary: any;
  results: TestResult[];
  datetime: string;
  created_at: string;
  description: string;
  name: string;
  summary: Summary;
};

const TYPE_MAP: Record<string, string> = {
  "total-wbc": "شمارش کل گلبول سفید",
  "dfi": "شاخص شکست DNA",
  "diff-wbc": "تفکیک گلبول‌های سفید",
};

export default defineComponent({
  name: "PatientTests",
  components: { Carousel, Slide, Pagination, Navigation, LoadingScreen },
  data() {
    return {
      isLoading: true,
      active: false,
      tests: [] as TestRecord[],
      test: null as TestRecord | null,
      pagination: {
        current_page: 1,
        last_page: 1,
        per_page: 50,
        total: 0,
      },
      editModal: false,

      editForm: {
        id: null as number | null,
        name: "",
        description: "",
      },

      editingId: null as number | null,
      editingField: null as 'name' | 'description' | null,
      editingNameValue: '',
      editingDescriptionValue: '',
    };
  },
  computed: {
    middlePages(): number[] {
      const pages: number[] = [];
      let startPage = Math.max(2, this.pagination.current_page - 1);
      let endPage = Math.min(this.pagination.last_page - 1, this.pagination.current_page + 1);
      for (let i = startPage; i <= endPage; i++) pages.push(i);
      return pages;
    },
    slidesForTest(): Array<{ id: string; image_path: string; label?: string; description?: string }> {
      if (!this.test) return [];
      const main = { id: `main-${this.test.id}`, image_path: this.test.image_path, label: "main" };
      const rest = (this.test.results || [])
        .filter((r: any) => r && r.image_path)
        .map((r: any) => ({ id: String(r.id), image_path: r.image_path, label: r.label, description: r.description }));
      return [main, ...rest];
    },
    carouselKey(): string {
      if (!this.test) return "none";
      return `${this.test.id}-${(this.test.results || []).length}`;
    },
    summaryLabel(): string {
      if (!this.test) return "";
      const s = this.test.summary || {};
      const entries = Object.entries(s)
        .filter(([_, v]) => v !== null && v !== "")
        .filter(([k]) => k.startsWith('dfi_') || k === 'count' || k === 'fragmented_dna_percentage')
        .map(([k, v]) => `${k}: ${this.formatPercent(v)}`);
      return entries.join(" — ");
    },
  },
  methods: {
    faType(t: string): string {
      return TYPE_MAP[t] || t;
    },
    parseSummary(raw: any): Summary {
      if (!raw) return {};
      if (typeof raw === "number" || /^[0-9]+$/.test(raw)) return { count: raw };
      if (typeof raw === "string") {
        try {
          const parsed = JSON.parse(raw);
          return typeof parsed === "object" && parsed !== null ? parsed : { value: raw };
        } catch {
          return { value: raw };
        }
      }
      if (typeof raw === "object") return raw;
      return {};
    },
    normalizeTest(item: any): TestRecord {
      const summary = this.parseSummary(item.result_summary);
      return {
        id: item.id,
        image_path: item.image_path,
        type: item.type,
        result_summary: item.result_summary,
        results: Array.isArray(item.results) ? item.results : [],
        datetime: item.datetime || "",
        created_at: item.created_at || "",
        description: item.description || "",
        name: item.name || "",
        summary,
      };
    },
    async fetchTests(page = 1) {
      this.isLoading = true;
      try {
        const { data } = await axios.get(`patient/tests?page=${page}`);
        const payload = data?.data || [];
        this.tests = payload.map((x: any) => this.normalizeTest(x));

        this.pagination = {
          current_page: data?.meta?.current_page ?? 1,
          last_page: data?.meta?.last_page ?? 1,
          per_page: data?.meta?.per_page ?? 50,
          total: data?.meta?.total ?? this.tests.length
        };
      } catch (error) {
        console.error("Error fetching tests:", error);
      } finally {
        this.isLoading = false;
      }
    },
    show(index: number) {
      this.test = this.tests[index];
      this.active = true;
    },
    closeModal() {
      this.active = false;
      this.$nextTick(() => (this.test = null));
    },
    zoom(path: string) {
      window.open(path, "_blank");
    },
    changePage(page: number) {
      if (page >= 1 && page <= this.pagination.last_page) {
        this.fetchTests(page);
      }
    },
    formatPercent(v: string | number): string {
      const num = typeof v === "string" ? parseFloat(v) : v;
      if (isNaN(Number(num))) return String(v);
      return num % 1 === 0 ? String(num) : num.toFixed(1);
    },
    openEditModal(test: TestRecord) {
      this.editForm = {
        id: test.id,
        name: test.name || "",
        description: test.description || "",
      };

      this.editModal = true;
    },

    closeEditModal() {
      this.editModal = false;
    },

    async updateTest() {
      try {
        await axios.put(`/patient/tests/${this.editForm.id}`, {
          name: this.editForm.name,
          description: this.editForm.description,
        });


        const index = this.tests.findIndex(
          (x) => x.id === this.editForm.id
        );

        if (index !== -1) {
          this.tests[index].name = this.editForm.name;
          this.tests[index].description = this.editForm.description;
        }

        this.closeEditModal();

      } catch (error) {
        console.error(error);
      }
    },

    startInlineEdit(test: TestRecord, field: 'name' | 'description') {
      this.editingId = test.id;
      this.editingField = field;
      this.editingNameValue = test.name ?? '';
      this.editingDescriptionValue = test.description ?? '';
    },

    resetInlineEdit() {
      this.editingId = null;
      this.editingField = null;
      this.editingNameValue = '';
      this.editingDescriptionValue = '';
    },

    async saveInlineEdit(test: TestRecord) {
      if (!this.editingId || !this.editingField) return;

      const field = this.editingField;
      const value = field === 'name' ? this.editingNameValue : this.editingDescriptionValue;
      const oldValue = field === 'name' ? test.name : test.description;
      const nameValue = this.editingNameValue;
      const descriptionValue = this.editingDescriptionValue;

      if (field === 'name') {
        test.name = value;
      } else {
        test.description = value;
      }

      this.resetInlineEdit();

      try {
        await axios.put(`/patient/tests/${test.id}`, {
          name: nameValue,
          description: descriptionValue,
        });
      } catch (error) {
        console.error(error);
        if (field === 'name') {
          test.name = oldValue;
        } else {
          test.description = oldValue;
        }
      }
    },
  },
  mounted() {
    this.fetchTests();
  },
});
